import {
  OwnershipIntersectableGeometry,
  useTitlesByLocationQuery,
} from "react-migration/domains/ownership/apollo/useTitlesByLocationQuery";

import Feature from "src/js/stores/user/Feature";
import { LayerTypePrintableProps } from "../../types";
import { Table } from "react-migration/components/TableDeprecated";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { useEffect } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import clsx from "clsx";

export function OwnershipPrintable({ selection, onLoaded, onUnmount }: LayerTypePrintableProps) {
  const { t } = useTranslation();
  const isUSUser = hasFeature(Feature.usAccess);

  const geometry = selection.feature?.geometry;
  const { titlesByLocation, loading } = useTitlesByLocationQuery({
    location:
      geometry &&
      ({
        type: geometry.type,
        coordinates: geometry.coordinates,
      } as OwnershipIntersectableGeometry),
  });

  useEffect(() => {
    if (!loading) {
      onLoaded();
    }
  }, [loading, onLoaded]);

  useEffect(() => {
    return () => {
      onUnmount();
    };
  }, [onUnmount]);

  const ownershipRows = titlesByLocation.map((title) => ({
    id: title.id,
    company_name: (title.owners?.[0]?.company ?? title.owners?.[0]?.person)?.name,
    address: title.owners?.[0]?.company?.addresses?.[0] ?? title.addresses?.[0],
    company_number: title.owners?.[0]?.company?.company_no,
    proprietorship: title.owners?.[0]?.company?.proprietorship,
    title_no: isUSUser ? title.parcel_identifier : title.title_no,
    tenure: title.tenure,
  }));

  const mainOwner = ownershipRows[0];

  const renderCell = (value?: string, className?: string) => (
    <span className={clsx("print:whitespace-pre-line print:w-[250px]", className)}>{value}</span>
  );

  return (
    <div data-testid="ownership-report" className="mb-4 print-landscape">
      <h3>{t("sites.card.site_report.ownership")}</h3>
      <div>
        {t("sites.card.site_report.ownership.number_of_titles")}: {ownershipRows.length}
      </div>
      <div className="mb-4">
        {t("sites.card.site_report.ownership.most_significant_owner")}: {mainOwner?.company_name ?? "Unknown"}
      </div>
      <Table
        size="small"
        primaryKey="id"
        columns={[
          {
            label: t("sites.card.site_report.ownership.address"),
            render: ({ address }) => renderCell(address),
            grow: true,
          },
          {
            label: t("sites.card.site_report.ownership.company_name"),
            render: ({ company_name }) => renderCell(company_name),
            grow: true,
          },
          {
            label: t("sites.card.site_report.ownership.company_number"),
            rowKey: "company_number",
            grow: true,
            align: "center",
          },
          {
            label: t("sites.card.site_report.ownership.proprietorship"),
            render: ({ proprietorship }) => renderCell(proprietorship),
            grow: true,
          },
          {
            label: t("sites.card.site_report.ownership.title_number"),
            render: ({ title_no }) => renderCell(title_no, "print:wrap-anywhere!"),
            grow: true,
            align: "center",
          },
          {
            label: t("sites.card.site_report.ownership.tenure"),
            rowKey: "tenure",
            grow: true,
            align: "center",
          },
        ]}
        rows={ownershipRows}
        tableClassName="block overflow-x-auto print:table print:table-fixed"
      />
    </div>
  );
}
